import {
  DatePicker,
  Field,
  Input,
  RadioGroup,
  Select,
  Spin,
  TextArea,
} from '@/components/commons';
import { Badge, Button, Form, Image, Upload } from 'antd';
import { useEffect, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Moment } from 'moment';
import { useFormContext } from 'react-hook-form';
import { CloseOutlined } from '@ant-design/icons';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import 'react-quill/dist/quill.snow.css';
import { Status, statusOptions } from '@/utils';
import { ContentImage } from '../../OfferList';
import { useQuery } from 'react-query';
import { ENDPOINT } from '@/services/endpoint';
import { PartnerService } from '@/services/partner';
import { TagService } from '@/services/tag';
import moment from 'moment';
import { IGetDetailOfferResponse } from '@/types/response';
import { TagType } from '@/types/response/tag';

interface IProps {
  isLoading: boolean;
  offer?: IGetDetailOfferResponse;
}

export const OfferForm: React.FC<IProps> = ({ isLoading, offer }) => {
  const { trigger, watch, setValue, setError, clearErrors } = useFormContext();

  const { data: partnersRes } = useQuery([ENDPOINT.PARTNER.GET_LIST], () =>
    PartnerService.getList({ page: 1, limit: 100 } as any),
  );

  const { data: tagsRes } = useQuery(
    [ENDPOINT.TAG.GET_LIST, TagType.OFFER],
    () => TagService.getList(TagType.OFFER),
  );

  const partnerOptions = (partnersRes?.data?.data || []).map((p: any) => ({
    value: Number(p?.id ?? p?.value),
    label: String(p?.companyName ?? p?.label ?? ''),
  }));

  const tagOptions = (tagsRes?.data || []).map((t: any) => ({
    value: String(t?.name ?? t?.label ?? t),
    label: String(t?.name ?? t?.label ?? t),
  }));

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(dayjs().startOf('day'));
  };

  const disabledTime = (current: Dayjs) => {
    if (!current) return {};

    const now = dayjs();
    const selectedDate = dayjs(current);

    if (selectedDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selectedDate.isSame(now, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < now.hour(); i++) {
        disabledHours.push(i);
      }

      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = [];
          for (let i = 0; i <= now.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };

      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }

    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };

  const coverImage: ContentImage | null = watch('coverImage');
  const isDisabled = watch('isDisabled');
  const useCoupon = Number(watch('useCoupon')) === 1;
  const status = watch('status');
  const couponOptions = [
    { label: 'あり', value: 1 },
    { label: 'なし', value: 0 },
  ];

  const beforeUpload = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > FILE_SIZE_LIMIT) {
      setError('attachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('attachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('attachment');
    handleUpload(file);

    return true;
  };

  const handleUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setValue('coverImage', {
        previewUrl: reader.result as string,
        file,
      });
    };
    reader.readAsDataURL(file);
  };

  const isPublic = useMemo(
    () =>
      !!offer &&
      offer.status === Status.PUBLIC &&
      moment(offer.publicAt).isSameOrBefore(moment()),
    [offer],
  );

  useEffect(() => {
    if (isPublic && status === Status.PUBLIC && offer?.publicAt) {
      setValue('publicAt', moment(offer.publicAt));
    }
  }, [status, isPublic, offer, setValue]);

  useEffect(() => {
    if (!useCoupon) {
      setValue('couponCode', undefined);
      setValue('couponExpiredAt', undefined);
      setValue('ctaText', undefined);
    }
  }, [useCoupon]);

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading}>
        <label className='title-calendar font-semibold'>
          カバー画像
          <Badge status='error' className='ml-2 mb-1' />
        </label>
        <div className='flex items-baseline gap-2 pt-2'>
          {!coverImage ? (
            <div className='border-solid rounded border-2 border-zinc-300  w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                beforeUpload={(file: File) => beforeUpload(file)}
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                showUploadList={false}
              >
                <Button
                  type='default'
                  shape='default'
                  size='middle'
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                  disabled={isDisabled}
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          ) : (
            <div className='relative inline-block'>
              <Image
                height={100}
                src={coverImage?.previewUrl}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={() => setValue('coverImage', null)}
                className={`absolute -top-2 -right-2 z-10 bg-red-500 border-none ${isDisabled && 'hidden'}`}
              />
            </div>
          )}
        </div>
        <label className='title-calendar font-medium text-xs'>
          <span className='font-bold mr-1'>※</span>
          1.8:1(~375×211 px)の写真（正方形）をアップロードしてください
        </label>
        <Field name='attachment' className='mt-[-34px] absolute'>
          <></>
        </Field>

        <Field label='タイトル' name='title' className='mt-6' required>
          <Input placeholder='タイトル' maxLength={40} disabled={isDisabled} />
        </Field>

        <Field label='説明文章' name='description' required>
          <TextArea
            placeholder='説明文章'
            maxLength={2000}
            disabled={isDisabled}
          />
        </Field>

        <Field label='提供企業' name='partnerId' required>
          <Select
            placeholder='提供企業'
            style={{ width: '100%' }}
            options={partnerOptions}
            filterOption={(input, option) =>
              String(option?.label ?? option?.children ?? '')
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            disabled={isDisabled}
          />
        </Field>

        <Field label='タグ' name='tagNames' required>
          <Select
            mode='tags'
            placeholder='タグ'
            style={{ width: '100%' }}
            options={tagOptions}
            disabled={isDisabled}
          />
        </Field>

        <Field label='クーポン利用あり・なし' name='useCoupon' required>
          <RadioGroup options={couponOptions} disabled={isDisabled} />
        </Field>

        {useCoupon && (
          <>
            <Field label='クーポンコード' name='couponCode' required>
              <Input
                placeholder='クーポンコード'
                maxLength={15}
                disabled={isDisabled}
              />
            </Field>

            <Field label='クーポン有効期限' name='couponExpiredAt' required>
              <DatePicker
                disabledDate={disabledDate}
                disabledTime={disabledTime}
                showTime
                onCalendarChange={(e) => {
                  setValue(
                    'couponExpiredAt',
                    e ? (e as Moment).second(0) : undefined,
                  );
                  trigger('couponExpiredAt');
                }}
                disabled={isDisabled}
              />
            </Field>
          </>
        )}

        <Field label='詳細' name='details'>
          {/* <ReactQuill
            theme='snow'
            modules={modulesReactQuill}
            readOnly={isDisabled}
          /> */}
          <Input placeholder='詳細' maxLength={500} disabled={isDisabled} />
        </Field>

        <Field label='CTA文言' name='ctaText' required>
          <Input placeholder='CTA文言' maxLength={30} disabled={isDisabled} />
        </Field>

        <Field label='遷移先' name='linkUrl' required>
          <Input placeholder='遷移先' maxLength={2000} disabled={isDisabled} />
        </Field>

        <div className='flex items-baseline gap-2'>
          <div className='flex-1'>
            <span className='title-calendar font-semibold'>
              公開日時
              <Badge status='error' className='ml-2' />
            </span>
          </div>
          <div className='flex-1'>
            <span className='title-calendar font-semibold ml-2'>
              公開終了日時
            </span>
          </div>
        </div>

        <div className='flex items-baseline gap-1 pt-2'>
          <Field name='publicAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('publicAt', e ? (e as Moment).second(0) : undefined);
                trigger('publicAt');
                trigger('publicEndAt');
              }}
              disabled={isDisabled || (isPublic && status !== Status.DRAFT)}
            />
          </Field>
          <span>～</span>
          <Field name='publicEndAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue(
                  'publicEndAt',
                  e ? (e as Moment).second(0) : undefined,
                );
                trigger('publicEndAt');
              }}
              disabled={isDisabled}
            />
          </Field>
        </div>

        <Field label='公開ステータス' name='status' className='w-full'>
          <RadioGroup options={statusOptions} disabled={isDisabled} />
        </Field>
      </Spin>
    </Form>
  );
};
